import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Info } from "lucide-react";

// Small "i" that reveals a short explainer. Works on hover (desktop) and tap (mobile).
// Popup renders in a portal so it's never clipped by overflow-hidden parents.
export const InfoTip = ({ text, testid = "info-tip" }) => {
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const open = hovered || pinned;

  const place = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const width = 224;
    let left = r.left + r.width / 2 - width / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
    setCoords({ top: r.bottom + 8, left });
  }, []);

  useEffect(() => {
    if (!open) return;
    place();
    const onScroll = () => { setHovered(false); setPinned(false); };
    const onDoc = (e) => { if (btnRef.current && !btnRef.current.contains(e.target)) setPinned(false); };
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    document.addEventListener("click", onDoc);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
      document.removeEventListener("click", onDoc);
    };
  }, [open, place]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        data-testid={testid}
        aria-label="What does this mean?"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={(e) => { e.stopPropagation(); setPinned((p) => !p); }}
        className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-[#334155] bg-[#0F1424] text-[#94A3B8] transition-colors hover:border-[#00F0FF]/60 hover:text-[#00F0FF]"
      >
        <Info size={10} strokeWidth={2.5} />
      </button>
      {open && createPortal(
        <span
          data-testid={`${testid}-popup`}
          onClick={(e) => e.stopPropagation()}
          style={{ position: "fixed", top: coords.top, left: coords.left, width: 224, zIndex: 9999 }}
          className="rounded-lg border border-[#1E293F] bg-[#0B1120] px-3 py-2 text-left text-[11px] font-normal normal-case leading-snug text-[#CBD5E1] shadow-[0_8px_30px_rgba(0,0,0,0.6)]"
        >
          {text}
        </span>,
        document.body
      )}
    </>
  );
};
